import Button from '@ui/buttons/Button'
import { ModalBottom } from '@ui/components/Modal'
import FormInputDate from '@ui/forms/Date/FormInputDate'
import FormInput from '@ui/forms/Input/FormInput'
import FormSelect from '@ui/forms/Select/FormSelect'
import { useCountries } from 'hooks/options/useCountries'
import { useConfirmCustomer } from '@user/customer/confirmation/useConfirmCustomer'
import React from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import cn from '@ui/utils/cn'
import useInvoiceForm from './useInvoiceForm'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { BirthdateValues } from '@ca/common'

interface Props {
  open: boolean
  close: () => void
}

export default function OrderInvoiceModal({ open, close }: Props) {
  const { t } = useTranslation(['ui', 'modals', 'user'])

  const { isInvoiceRequired } = useConfirmCustomer()

  const countries = useCountries()
  const { form, create, patch, onClose, onSubmit, invoice } = useInvoiceForm({
    open,
    close,
  })

  const isChanged = form.formState.isDirty
  const isValid = form.formState.isValid
  const styleChanged = useAnimatedOpacity(isChanged || !isValid)
  const isInvalid = !invoice?.id

  return (
    <ModalBottom
      open={open}
      close={onClose}
      title={t('modals:invoice.title')}
      desc={
        isInvoiceRequired
          ? t('modals:invoice.under18.desc')
          : t('modals:invoice.desc')
      }
      body={
        <FormProvider {...form}>
          <View className={cn('gap-6 px-16')}>
            {/* <Checkbox
              disabled={isInvoiceRequired}
              value={formEnabled}
              onChange={() => setWithInvoice((v) => !v)}
              label={t('modals:invoice.use')}
            /> */}
            <View
              className={cn(
                'gap-6'
                // !formEnabled && 'pointer-events-none opacity-25'
              )}
            >
              <FormInput
                control={form.control}
                label={t('user:firstName')}
                name="firstName"
                next="lastName"
                autoCapitalize="words"
              />
              <FormInput
                control={form.control}
                label={t('user:lastName')}
                name="lastName"
                next="vatId"
                autoCapitalize="words"
              />
              <FormInputDate
                control={form.control}
                label={t('user:birthDate')}
                name="birth_date"
                min={BirthdateValues.min}
                max={BirthdateValues.max}
                defaultDate={BirthdateValues.default}
              />
              <FormInput
                control={form.control}
                label={t('user:vat')}
                name="vatId"
                next="email"
              />
              <FormInput
                control={form.control}
                label={t('user:email')}
                name="email"
                next="mobile"
              />
              <FormInput
                control={form.control}
                label={t('user:mobile')}
                name="mobile"
                next="street"
              />
              <View className="flex-row gap-6">
                <FormInput
                  control={form.control}
                  label={t('user:street')}
                  name="street"
                  next="street2"
                  className="w-3/5"
                />
                <FormInput
                  control={form.control}
                  label={t('user:street2')}
                  name="street2"
                  next="city"
                  className="flex-1"
                />
              </View>
              <View className="flex-row gap-6">
                <FormInput
                  control={form.control}
                  label={t('user:city')}
                  name="city"
                  next="zip"
                  className="w-3/5"
                />
                <FormInput
                  control={form.control}
                  label={t('user:zip')}
                  name="zip"
                  className="flex-1"
                />
              </View>
              <FormSelect
                control={form.control}
                label={t('user:country')}
                name="country_code"
                options={countries}
                placeholder={t('user:address.country')}
              />
            </View>
          </View>
        </FormProvider>
      }
      buttons={
        <>
          <Button
            variant="outlined"
            disabled={isInvalid}
            label={t('ui:close')}
            onPress={onClose}
          />
          <Button
            variant="filled"
            label={t('ui:save')}
            iconEnd="arrow-narrow-right"
            loading={patch.isPending || create.isPending}
            onPress={onSubmit}
            style={styleChanged}
          />
        </>
      }
    />
  )
}
